import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { completeOnboarding } from "../api/endpoints";
import { CURRENT_HEALTH_ACK_VERSION, CURRENT_TERMS_VERSION } from "../api/consent";
import { useAuth } from "../hooks/useAuth";
import { Button, Card, ErrorMessage } from "../components/ui";

function Check({
  checked,
  onChange,
  testId,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mt-4 flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600"
        data-testid={testId}
      />
      <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
    </label>
  );
}

/**
 * Health acknowledgement and terms. Blocking by design: training advice is
 * being given, so the acknowledgement has to be explicit and recorded against
 * a version rather than assumed.
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [terms, setTerms] = useState(false);
  const [health, setHealth] = useState(false);
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const ready = terms && health && age;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await completeOnboarding({
        termsVersion: CURRENT_TERMS_VERSION,
        healthAckVersion: CURRENT_HEALTH_ACK_VERSION,
        ageConfirmed: true,
      });
      await refreshUser();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-xl px-4 py-10" data-testid="onboarding-page">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Before you start
      </h1>

      <Card className="mt-4 p-5">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Load &amp; Flow prescribes kettlebell and bodyweight training. It isn't medical advice,
          and it can't see your health history.
        </p>

        <Check checked={health} onChange={setHealth} testId="onboarding-check-health">
          I'm well enough to train, and I'll stop and seek advice if something hurts or feels
          wrong.
        </Check>

        <Check checked={terms} onChange={setTerms} testId="onboarding-check-terms">
          I accept the{" "}
          <Link to="/terms" className="text-emerald-700 underline dark:text-emerald-400">
            terms of use
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-emerald-700 underline dark:text-emerald-400">
            privacy policy
          </Link>
          .
        </Check>

        <Check checked={age} onChange={setAge} testId="onboarding-check-age">
          I'm 18 or over.
        </Check>

        {error && (
          <div className="mt-4">
            <ErrorMessage error={error} />
          </div>
        )}

        <Button
          className="mt-5"
          onClick={() => void submit()}
          disabled={!ready || busy}
          data-testid="onboarding-button-submit"
        >
          {busy ? "Saving…" : "Start training"}
        </Button>
      </Card>
    </main>
  );
}
